import React, { useState, useEffect } from 'react';
import { creatorAPI, getAuthHeaders } from '../../services/api';
import ChannelClaimWizard from './ChannelClaimWizard';
import CreatorOverviewPanel from './CreatorOverviewPanel';
import OfficialPicksPanel from './OfficialPicksPanel';
import CreatorReports from './CreatorReports';
import DiscoveryBoostPanel from './DiscoveryBoostPanel';
import {
  BarChart3, Star, Flag, CheckCircle, Loader, Eye, Flame, Heart,
  ExternalLink, Users, Video, TrendingUp, Settings, ChevronRight, Zap, X,
  Youtube, Sparkles, Send, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const CreatorWaitlistOverlay = ({ currentUser, claim, onClaimUpdate }) => {
  const [email, setEmail] = useState(currentUser?.email || '');
  const [channelName, setChannelName] = useState('');
  const [channelUrl, setChannelUrl] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/creator/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email, channelName, channelUrl }),
      });
      const data = await res.json();
      setStatus(data.alreadyRegistered ? 'already' : 'success');
    } catch {
      setStatus('idle');
    }
  };

  const done = status === 'success' || status === 'already';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', position: 'relative', overflow: 'hidden' }}>

      {/* ── Dimmed background: the real onboarding wizard ── */}
      <div style={{ opacity: 0.12, filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none', padding: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
          <span style={{ fontSize: '24px', fontWeight: '700', color: '#FFF' }}>Creator Studio</span>
          <span style={{
            fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px',
            background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', color: '#FFF',
            padding: '3px 10px', borderRadius: '4px',
          }}>EARLY ACCESS</span>
        </div>
        <ChannelClaimWizard currentUser={currentUser} claim={claim} onClaimUpdate={onClaimUpdate} />
      </div>

      {/* ── Centered waitlist card ── */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
      }}>
        <div style={{
          width: '100%', maxWidth: '480px',
          background: 'linear-gradient(160deg, #12121e 0%, #0e0e1a 100%)',
          border: '1px solid rgba(139,92,246,0.25)',
          borderRadius: '20px',
          padding: '40px 36px',
          boxShadow: '0 0 60px rgba(139,92,246,0.12), 0 24px 48px rgba(0,0,0,0.6)',
          textAlign: 'center',
        }}>

          {/* Icon */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '16px', margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(139,92,246,0.4)',
          }}>
            <Youtube size={28} color="#FFF" />
          </div>

          {/* Heading */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px', fontWeight: '700', color: '#FFF' }}>Creator Studio</span>
            <span style={{
              fontSize: '9px', fontWeight: '700', letterSpacing: '1px',
              background: 'rgba(139,92,246,0.2)', color: '#A78BFA',
              border: '1px solid rgba(139,92,246,0.3)',
              padding: '2px 8px', borderRadius: '4px',
            }}>COMING SOON</span>
          </div>

          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6', margin: '0 0 28px' }}>
            We're finishing the last details on something built for creators like you — channel verification, discoverability tools, official picks, and more. Join the whitelist and be first in line when we open the doors.
          </p>

          {!done ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#FFF', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <input
                type="text"
                value={channelName}
                onChange={e => setChannelName(e.target.value)}
                placeholder="Your channel name (optional)"
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#FFF', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <input
                type="url"
                value={channelUrl}
                onChange={e => setChannelUrl(e.target.value)}
                placeholder="YouTube channel URL (optional)"
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#FFF', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                disabled={status === 'loading' || !email}
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                  background: status === 'loading' ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                  color: '#FFF', fontSize: '14px', fontWeight: '700', cursor: status === 'loading' ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 4px 16px rgba(139,92,246,0.35)', marginTop: '4px',
                }}
              >
                <Send size={15} />
                {status === 'loading' ? 'Joining...' : 'Join the Whitelist'}
              </button>
            </form>
          ) : (
            <div style={{
              padding: '24px', borderRadius: '12px',
              background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
            }}>
              <CheckCircle2 size={32} color="#22C55E" style={{ marginBottom: '12px' }} />
              <p style={{ color: '#22C55E', fontWeight: '600', fontSize: '15px', margin: '0 0 6px' }}>
                {status === 'already' ? "You're already on the list!" : "You're on the list!"}
              </p>
              <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                We'll reach out at <strong style={{ color: '#9CA3AF' }}>{email}</strong> when Creator Studio is ready for you.
              </p>
            </div>
          )}

          <p style={{ fontSize: '11px', color: '#374151', marginTop: '20px', marginBottom: 0 }}>
            No spam. Just one email when it's your turn.
          </p>
        </div>
      </div>
    </div>
  );
};

const BoostCTABanner = ({ onGoToBoost, onDismiss }) => (
  <div style={{
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(251,191,36,0.08) 100%)',
    border: '1px solid rgba(139,92,246,0.3)',
    borderRadius: '14px',
    padding: '20px 24px',
    marginBottom: '28px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    overflow: 'hidden',
  }}>
    {/* Glow accent */}
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
      background: 'linear-gradient(90deg, #8B5CF6, #FBBF24, #8B5CF6)',
    }} />

    {/* Icon */}
    <div style={{
      width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
      background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 16px rgba(139,92,246,0.4)',
    }}>
      <Zap size={24} style={{ color: '#FFF' }} />
    </div>

    {/* Text */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <span style={{ fontSize: '15px', fontWeight: '700', color: '#FFF' }}>
          Make your channel known
        </span>
        <span style={{
          fontSize: '9px', fontWeight: '700', letterSpacing: '1px',
          background: 'rgba(251,191,36,0.15)', color: '#FBBF24',
          border: '1px solid rgba(251,191,36,0.3)',
          padding: '2px 7px', borderRadius: '4px',
        }}>EARLY ACCESS</span>
      </div>
      <p style={{ fontSize: '13px', color: '#888', margin: 0, lineHeight: '1.5' }}>
        Appear first in reaction overlays · Carousel rotation · Featured spots · $29.90/month founding price
      </p>
    </div>

    {/* CTA button */}
    <button
      onClick={onGoToBoost}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
        background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
        border: 'none', color: '#FFF', padding: '10px 20px',
        borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '700',
        boxShadow: '0 4px 16px rgba(139,92,246,0.35)',
        whiteSpace: 'nowrap',
      }}
    >
      <Zap size={15} /> Boost My Channel
    </button>

    {/* Dismiss */}
    <button
      onClick={onDismiss}
      style={{
        position: 'absolute', top: '10px', right: '10px',
        background: 'none', border: 'none', color: '#555',
        cursor: 'pointer', padding: '4px',
      }}
    >
      <X size={14} />
    </button>
  </div>
);

const CreatorDashboard = ({ currentUser }) => {
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [channel, setChannel] = useState(null);
  const [claim, setClaim] = useState(null);
  const [subTab, setSubTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [quickStats, setQuickStats] = useState(null);
  const [boostStatus, setBoostStatus] = useState(null);
  const [ctaDismissed, setCtaDismissed] = useState(() => sessionStorage.getItem('boost_cta_dismissed') === '1');

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await creatorAPI.getMe();
      setCreatorProfile(data.profile);
      setChannel(data.channel);
      setClaim(data.claim);
      // Fetch quick stats for sidebar
      try {
        const dashData = await creatorAPI.getDashboard();
        setQuickStats(dashData.dashboard);
      } catch (e) { /* non-critical */ }
      try {
        const boostData = await creatorAPI.getBoostStatus();
        setBoostStatus(boostData.boost);
      } catch (e) { /* non-critical */ }
    } catch (err) {
      console.error('Failed to load creator profile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const isVerified = creatorProfile?.claimStatus === 'verified';

  const fmtNum = (n) => {
    if (!n) return '0';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return n.toLocaleString();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0A0A0F', color: '#E5E5E5',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={28} style={{ animation: 'spin 1s linear infinite', color: '#8B5CF6', marginBottom: '12px' }} />
          <div style={{ fontSize: '15px', color: '#888' }}>Loading Creator Studio...</div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return <CreatorWaitlistOverlay currentUser={currentUser} claim={claim} onClaimUpdate={fetchProfile} />;
  }

  const subTabs = [
    { id: 'overview', icon: BarChart3, label: 'Overview' },
    { id: 'picks', icon: Star, label: 'Official Picks' },
    { id: 'boost', icon: Zap, label: 'Discovery Boost', badge: '⚡' },
    { id: 'reports', icon: Flag, label: 'Reports' },
  ];

  const sidebarStats = [
    { icon: Eye, label: 'Total Clicks', value: quickStats?.totalClicks || 0 },
    { icon: Flame, label: 'Peak Moments', value: quickStats?.peakMomentsCount || 0 },
    { icon: Heart, label: 'Favorites', value: quickStats?.channelFavorites || 0 },
    { icon: Video, label: 'Videos Indexed', value: quickStats?.indexedVideos || 0 },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', color: '#E5E5E5', display: 'flex' }}>
      {/* ── Left Sidebar ── */}
      <div style={{
        width: '280px', flexShrink: 0, borderRight: '1px solid #1A1A2E',
        display: 'flex', flexDirection: 'column', padding: '24px 0',
        background: 'linear-gradient(180deg, #0D0D15 0%, #0A0A0F 100%)',
      }}>
        {/* Channel card */}
        <div style={{ padding: '0 20px', marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            {channel?.avatarUrl ? (
              <img src={channel.avatarUrl} alt="" style={{
                width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover',
                border: '2px solid #8B5CF6', boxShadow: '0 0 12px rgba(139,92,246,0.3)',
              }} />
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', fontWeight: '700', color: '#FFF',
              }}>{(channel?.channelName?.[0] || 'C').toUpperCase()}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '15px', fontWeight: '600', color: '#FFF',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                {channel?.channelName || 'Creator Studio'}
                <CheckCircle size={14} style={{ color: '#8B5CF6', flexShrink: 0 }} />
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {channel?.subscriberCount ? `${fmtNum(channel.subscriberCount)} subscribers` : 'Creator'}
              </div>
            </div>
          </div>

          {/* Beta tag */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px', borderRadius: '6px',
            background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.2)',
            fontSize: '11px', color: '#A78BFA', fontWeight: '600', letterSpacing: '0.5px',
          }}>
            <Flame size={12} /> EARLY ACCESS
          </div>
        </div>

        {/* Navigation */}
        <div style={{ padding: '0 12px', marginBottom: '28px' }}>
          {subTabs.map(tab => {
            const active = subTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setSubTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '11px 14px', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '14px', marginBottom: '4px',
                  fontWeight: active ? '600' : '400',
                  background: active ? (tab.id === 'boost' ? 'rgba(251,191,36,0.1)' : 'rgba(139,92,246,0.15)') : 'transparent',
                  color: active ? (tab.id === 'boost' ? '#FBBF24' : '#C4B5FD') : (tab.id === 'boost' ? '#A0785A' : '#777'),
                  transition: 'all 0.15s',
                  borderLeft: active ? `3px solid ${tab.id === 'boost' ? '#FBBF24' : '#8B5CF6'}` : '3px solid transparent',
                }}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.id === 'boost' && (
                  <span style={{
                    marginLeft: 'auto', fontSize: '9px', fontWeight: '700',
                    background: 'rgba(251,191,36,0.2)', color: '#FBBF24',
                    padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.5px',
                  }}>NEW</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div style={{ padding: '0 20px', marginBottom: '24px' }}>
          <div style={{ fontSize: '11px', color: '#555', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>
            Quick Stats
          </div>
          {sidebarStats.map((stat, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: i < sidebarStats.length - 1 ? '1px solid #151520' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#888' }}>
                <stat.icon size={14} style={{ color: '#8B5CF6' }} />
                {stat.label}
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#E5E5E5' }}>
                {fmtNum(stat.value)}
              </span>
            </div>
          ))}
        </div>

        {/* YouTube link */}
        {channel?.youtubeChannelId && (
          <div style={{ padding: '0 20px', marginTop: 'auto' }}>
            <a
              href={`https://youtube.com/channel/${channel.youtubeChannelId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 14px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.03)', border: '1px solid #1A1A2E',
                color: '#888', fontSize: '13px', textDecoration: 'none',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#CCC'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#888'; }}
            >
              <ExternalLink size={14} />
              View on YouTube
              <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
            </a>
          </div>
        )}
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 32px', borderBottom: '1px solid #1A1A2E',
          background: 'rgba(10,10,15,0.8)', backdropFilter: 'blur(8px)',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#FFF', margin: 0 }}>
              {subTab === 'overview' ? 'Analytics Overview'
                : subTab === 'picks' ? 'Official Picks'
                : subTab === 'boost' ? 'Discovery Boost'
                : 'Reports'}
            </h1>
            <p style={{ fontSize: '13px', color: '#555', margin: '2px 0 0' }}>
              {subTab === 'overview'
                ? 'Track your channel performance and engagement'
                : subTab === 'picks'
                ? 'Curate your best moments for fans'
                : subTab === 'boost'
                ? 'Amplify your reach across the ReactorHub platform'
                : 'Report issues and track resolutions'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {channel?.avatarUrl && (
              <img src={channel.avatarUrl} alt="" style={{
                width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
                border: '1px solid #2A2A3E',
              }} />
            )}
            <div style={{ fontSize: '13px', color: '#888' }}>
              {currentUser?.username || 'Creator'}
            </div>
          </div>
        </div>

        {/* Content area */}
        <div style={{ padding: '28px 32px' }}>
          {subTab === 'overview' && (
            <>
              {!ctaDismissed && (!boostStatus || boostStatus.status === 'cancelled' || boostStatus.status === 'expired') && (
                <BoostCTABanner
                  onGoToBoost={() => setSubTab('boost')}
                  onDismiss={() => {
                    setCtaDismissed(true);
                    sessionStorage.setItem('boost_cta_dismissed', '1');
                  }}
                />
              )}
              <CreatorOverviewPanel channelId={creatorProfile.claimedChannelId} />
            </>
          )}
          {subTab === 'picks' && (
            <OfficialPicksPanel channelId={creatorProfile.claimedChannelId} />
          )}
          {subTab === 'boost' && (
            <DiscoveryBoostPanel />
          )}
          {subTab === 'reports' && (
            <CreatorReports channelId={creatorProfile.claimedChannelId} />
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CreatorDashboard;
